/* eslint-disable no-unused-vars */
import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { addAlpha, ceillingObj, getRoomPositionAndSize, lightenColor } from "./floor-vacancy-utils";

const sensor = "se";
const openSpace = "op";
let heatmap;

const canUseWebGL = () =>
  typeof window !== "undefined" &&
  !!window.document.createElement("canvas").getContext("webgl");

/**
 * renderFloor
 * @param {HTMLElement} htmlElement
 * @param {WebGLRenderer} webRenderer
 * @param {Object} object
 * @param {Object} props
 * Renderiza objetos obj num elemento html passado como parametro
 */
function renderFloor(htmlElement, floorData, metric, props) {
  try {
    const webRenderer = new THREE.WebGLRenderer({ alpha: true });

    //Get With and Height of parent element to calculate the aspectRatio for camera.
    const width = htmlElement.clientWidth;
    const height = htmlElement.clientHeight;
    const aspectRatio = width / height;

    webRenderer.setClearColor(0x000000, 0);
    webRenderer.setSize(width, height);
    htmlElement.appendChild(webRenderer.domElement);

    let rooms = [];
    let positions = [];

    //Create Scene
    const scene = new THREE.Scene();

    //Define camera
    if (props?.camera?.aspectRatio == null) {
      props.camera = {
        ...props.camera,
        aspectRatio,
      };
    }
    const camera = createCamera(props?.camera);

    //Add ambLight if wanted
    if (props?.ambLight) {
      scene.add(createAmbientLight(props?.ambLight));
    }

    //Add lights
    if (props?.lights?.length > 0) {
      props.lights.forEach((light) => {
        scene.add(createPointLight(light));
      });
    }

    const objLoader = new OBJLoader();

    //Add rooms to scene
    addRoomsToFloor(
      objLoader,
      scene,
      positions,
      rooms,
      floorData.rooms,
      floorData.data,
      floorData.ceiling,
      metric
    );

    if (props?.onHover) {
      addHoverListener(
        htmlElement,
        rooms,
        camera,
        props.onHover ?? null,
        props.setHovered ?? null
      );
    }

    addClickListener(htmlElement, rooms, camera, props.onClick, props.onRightClick);

    const texOcclusion = new THREE.TextureLoader().load(floorData.occlusion);
    const texAlpha = new THREE.TextureLoader().load(floorData.alpha);

    //Add floor to scene
    objLoader.load(
      floorData.floor,
      (obj) => floorObj(obj, scene, texAlpha, texOcclusion),
      undefined,
      (error) => {
        throw new Error(
          `An error occurred while loading the OBJ file: ${error}`
        );
      }
    );

    //Add floorStructure(walls) to scene
    objLoader.load(floorData.structure, (obj) => {
      floorStructure(obj, scene, floorData, heatmap)
    });

    // Orbit controls
    let controls;
    if (props?.setOrbitControls) {
      controls = new OrbitControls(camera, webRenderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.25;
      controls.enableZoom = true;
      controls.enableRotate = false;
      controls.enablePan = false;
      props.setOrbitControls(controls);
    }

    //Render
    const animate = () => {
      requestAnimationFrame(animate);
      if (props?.orbitControls) {
        controls.update();
      }
      webRenderer.render(scene, camera);
    };
    animate();

    if (props?.handleResize) {
      // Handle window resize
      const handleResize = () => {
        const width = htmlElement.clientWidth;
        const height = htmlElement.clientHeight;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        webRenderer.setSize(width, height);
      };
      window.addEventListener("resize", handleResize);
    }
  } catch (error) {
    throw new Error(`Error in WebGLRenderer ${error}`);
  }
}

/**
 * renderObjects
 * @param {HTMLElement} htmlElement
 * @param {Object} objectsToLoad
 * @param {Object} metric
 * @param {Object} props
 * Renderiza objetos obj num elemento html passado como parametro
 */
function renderSimpleObjects(htmlElement, objectsToLoad, metric, props, t) {
  try {
    const webRenderer = new THREE.WebGLRenderer({ alpha: true });
    //Get With and Height of parent element to calculate the aspectRatio for camera.
    const width = htmlElement.clientWidth;
    const height = htmlElement.clientHeight;
    const aspectRatio = width / height;
    let objects = [];

    webRenderer.setClearColor(0x000000, 0);
    webRenderer.setSize(width, height);
    htmlElement.appendChild(webRenderer.domElement);

    //Create Scene
    const scene = new THREE.Scene();

    //Define camera
    if (props?.camera?.aspectRatio == null) {
      props.camera = {
        ...props.camera,
        aspectRatio,
      };
    }
    const camera = createCamera(props?.camera);
    //Add ambLight if wanted
    if (props?.ambLight) {
      scene.add(createAmbientLight(props?.ambLight));
    }

    //Add lights
    if (props?.lights?.length > 0) {
      props.lights.forEach((light) => {
        scene.add(createPointLight(light));
      });
    }

    let numberOfObjects = objectsToLoad.length;
    let gap = 5;
    const startX = - (numberOfObjects - 1) * gap / 2;
    let objNum = 0;
    const objLoader = new OBJLoader();
    objectsToLoad.forEach((objectToLoad) => {
      objLoader.load(
        objectToLoad.objFile,
        (object) => {
          let i = 1;
          object.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.material = new THREE.MeshPhysicalMaterial({
                color: objectToLoad.data?.[metric]?.color,
                reflectivity: 0.6,
                roughness: 0.6,
                specularIntensity: 0.2,
                map: new THREE.TextureLoader().load(
                  objectToLoad.occlusion ?? ""
                ),
              });
              child.name = `room_${i}`;
              child.label = t(objectToLoad?.data?.[metric]?.mouse_label)
              child.value = objectToLoad?.data?.[metric]?.mouse_over,
                child.floorId = objectToLoad?.floor_id;
            }
          });
          // Set the position of the loaded object
          if (objectToLoad.position) {
            object.position.set(
              objectToLoad.position.x || 0,
              startX + objNum * gap,
              objectToLoad.position.z || 0
            );
          }
          objects.push(object);
          scene.add(object);
          objNum++;
        },
        undefined,
        (error) => {
          throw new Error(
            `An error occurred while loading the OBJ file: ${error}`
          );
        }
      );
    });

    if (props?.onHover) {
      addHoverListener(
        htmlElement,
        objects,
        camera,
        props.onHover ?? null,
        props.setHovered ?? null
      );
    }

    addClickListener(htmlElement, objects, camera, props.onClick, props.onRightClick);

    // Orbit controls
    let controls;
    if (props?.setOrbitControls) {
      controls = new OrbitControls(camera, webRenderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.25;
      controls.enableZoom = false;
      controls.enableRotate = false;
      controls.enablePan = false;
      props.setOrbitControls(controls);
    }

    const animate = () => {
      requestAnimationFrame(animate);
      if (props?.orbitControls) {
        controls.update();
      }
      webRenderer.render(scene, camera);
    };

    animate();

    if (props?.handleResize) {
      // Handle window resize
      const handleResize = () => {
        const width = htmlElement.clientWidth;
        const height = htmlElement.clientHeight;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        webRenderer.setSize(width, height);
      };
      window.addEventListener("resize", handleResize);
    }
  } catch (error) {
    throw new Error(`Error in WebGLRenderer ${error}`);
  }
}

function orbitCamera(orbitControls, orbitCameraVal) {
  if (orbitControls != null) {
    orbitControls.enableRotate = orbitCameraVal;
    orbitControls.enablePan = orbitCameraVal;
    orbitControls.enableZoom = orbitCameraVal;
  }
}

/**
 * renderObjects
 * @param {HTMLElement} htmlElement
 * @param {String} classNameToRemove
 * Remove os children com classname igual a classNameToRemove de htmlElement
 */
function cleanRenders(htmlElement, classNameToRemove) {
  const children = Array.from(htmlElement.children);

  children.forEach((child) => {
    if (child.classList.contains(classNameToRemove)) {
      htmlElement.removeChild(child);
    }
  });
}

/**
 * addHoverListener
 * @param {HTMLElement} htmlElement
 * @param {[THREE.Object3D]} objects
 * @param {THREE.Camera} object
 * @param {Object} onHover
 * @param {Object} setHovered
 * Código necessário para calcular quando um objeto esta a ser hovered.
 */
function addHoverListener(htmlElement, objects, camera, onHover, setHovered) {
  //Create Raycaster
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  const onMouseMove = (event) => {
    //Get HtmlElemtent coordinates bounding in window;
    const rect = htmlElement.getBoundingClientRect();
    //Get mouse coordinates relative to HtmlElemtent;
    /* 
      In WebGL and three.js, the normalized device coordinates range from -1 to 1 in both axes:
      The x coordinate ranges from -1 on the left to 1 on the right.
      The y coordinate ranges from -1 at the bottom to 1 at the top. 
    */
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(objects);

    if (intersects?.length > 0) {
      const intersected = intersects[0].object;
      onHover
        ? onHover(
          intersected,
          event.clientX - rect.left,
          event.clientY - rect.top + 20
        )
        : null;
      setHovered ? setHovered(intersected) : null;
    } else {
      setHovered ? setHovered(null) : null;
    }
  };

  htmlElement.addEventListener("mousemove", onMouseMove);
}

/**
 * Add an onClick listener for objects in the scene.
 * @param {HTMLElement} htmlElement The HTML element where the scene is rendered.
 * @param {[THREE.Object3D]} objects The objects to detect clicks on.
 * @param {THREE.Camera} camera The camera used in the scene.
 * @param {Function} onClick Callback function to execute on click.
 */
function addClickListener(htmlElement, objects, camera, onClick, onRightClick) {
  // Create a Raycaster
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  const handleMouseClick = (event, callback) => {
    // Get the bounding rectangle of the HTML element
    const rect = htmlElement.getBoundingClientRect();

    // Map the mouse click to normalized device coordinates (-1 to +1)
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Update the raycaster with the camera and mouse position
    raycaster.setFromCamera(mouse, camera);

    // Check for intersections
    const intersects = raycaster.intersectObjects(objects, true);

    if (intersects.length > 0) {
      const clickedObject = intersects[0].object;
      // Execute the callback with the clicked object
      if (callback)
        callback(clickedObject);
    }
  };

  const onMouseClick = (event) => {
    if (event.button === 0) { // Left click
      handleMouseClick(event, onClick);
    }
  };

  const onMouseRightClick = (event) => {
    event.preventDefault(); // Prevent default context menu
    if (event.button === 2) { // Right click
      handleMouseClick(event, onRightClick);
    }
  };

  // Attach the click event listener
  htmlElement.addEventListener("click", onMouseClick);
  htmlElement.addEventListener("contextmenu", onMouseRightClick);
}

//#region render utils
function createCamera(props) {
  const camera = new THREE.PerspectiveCamera(
    props?.fov ?? undefined,
    props?.aspectRatio,
    props?.near ?? undefined,
    props?.far ?? undefined
  );
  camera.position.set(
    props?.position?.x ?? 25,
    props?.position?.y ?? 15,
    props?.position?.z ?? 18
  );
  camera.lookAt(
    props?.lookAt?.x ?? 0,
    props?.lookAt?.y ?? 0,
    props?.lookAt?.z ?? 0
  );
  return camera;
}

function createAmbientLight(props) {
  return new THREE.AmbientLight(
    props?.color ?? 0xffffff,
    props?.intensity ?? 0.2
  );
}

function createPointLight(light) {
  const createdLight = new THREE.PointLight(
    light?.color ?? 0xffffff,
    light?.intensity ?? 2000,
    light?.distance ?? 1000
  );
  createdLight.position.set(light?.x ?? 15, light?.y ?? 20, light?.z ?? -26);
  return createdLight;
}

function addRoomsToFloor(
  objLoader,
  scene,
  positions,
  rooms,
  roomsSrc,
  roomsData,
  ceilingSrc,
  type
) {
  let fadeIntensity = 10;
  objLoader.load(
    roomsSrc,
    (object) => {
      let roomMap = new Map();

      roomsData.forEach((room) =>
        roomMap.set(room.id, {
          [type]: room?.[type],
          mouse_label: room?.mouse_label,
          mouse_value: room?.mouse_value
        })
      );
      object.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          let materialOptions = {
            transparent: true,
            reflectivity: 0.6,
            roughness: 0.6,
            specularIntensity: 0.2,
            opacity: 0.8,
          };

          if (
            child.name &&
            typeof child.name === "string" &&
            child.name.endsWith(openSpace)
          ) {
            materialOptions.transparent = false;
            materialOptions.opacity = 1;
          }

          let color = roomMap.get(child.name)?.[type];

          if (color === null || child.name.endsWith(sensor)) {
            child.scale.setY(0);
            child.scale.setX(0);
            child.scale.setZ(0);
          } else {
            materialOptions.color = color;
          }

          child.material = new THREE.MeshPhysicalMaterial(materialOptions);

          child.geometry.computeBoundingBox();
          const center = new THREE.Vector3();
          child.geometry.boundingBox.getCenter(center);
          child.geometry.center();
          child.position.copy(center);
          child.label = roomMap?.mouse_label?.en
          child.value = roomMap?.mouse_value
          positions.push({
            id: child.name,
            position: child.position.clone(),
          });

          child.renderOrder = 0;

        }
      });
      rooms.push(object);
      scene.add(object);

      const scaleX = -50,
        scaleY = -60,
        circleRadius = 200,
        temperatureCanvas = document.createElement("canvas"),
        temperatureContext = temperatureCanvas.getContext("2d"),
        occupationCanvas = document.createElement("canvas"),
        occupationContext = occupationCanvas.getContext("2d"),
        canvasWidth = 1024,
        canvasHeight = 1024;

      temperatureCanvas.width = canvasWidth;
      temperatureCanvas.height = canvasHeight;
      occupationCanvas.width = canvasWidth;
      occupationCanvas.height = canvasHeight;

      temperatureContext.clearRect(
        0,
        0,
        temperatureCanvas.width,
        temperatureCanvas.height
      );
      occupationContext.clearRect(
        0,
        0,
        occupationCanvas.width,
        occupationCanvas.height
      );

      occupationContext.fillStyle = "rgba(211, 211, 211, 0.01)";
      occupationContext.fillRect(0, 0, canvasWidth, canvasHeight);

      let sensorWithOccupation = false;

      roomsData.forEach((room) => {
        const { id, occupation, temperature } = room;

        if (temperature == null) {
          return;
        }

        const { x: roomX, z: roomZ } = getRoomPositionAndSize(
          id,
          positions,
          scaleX,
          scaleY,
          canvasWidth,
          canvasHeight
        );

        if (id.endsWith(sensor) && occupation != null) {

          sensorWithOccupation = true;
          if (roomX !== undefined && roomZ !== undefined) {
            const color1 = addAlpha(occupation, 0.5),
              color2 = addAlpha(lightenColor(occupation, fadeIntensity), 0.9),
              color3 = addAlpha(
                lightenColor(occupation, fadeIntensity * 2),
                0.7
              ),
              color4 = addAlpha(
                lightenColor(occupation, fadeIntensity * 3),
                0.5
              ),
              color5 = addAlpha(
                lightenColor(occupation, fadeIntensity * 4),
                0.3
              ),
              color6 = addAlpha(
                lightenColor(occupation, fadeIntensity * 5),
                0.1
              ),
              gradient = occupationContext.createRadialGradient(
                roomX,
                roomZ,
                0,
                roomX,
                roomZ,
                circleRadius
              );
            gradient.addColorStop(0, color1);
            gradient.addColorStop(0.2, color2);
            gradient.addColorStop(0.4, color3);
            gradient.addColorStop(0.6, color4);
            gradient.addColorStop(0.8, color5);
            gradient.addColorStop(1, color6);

            occupationContext.fillStyle = gradient;
            occupationContext.beginPath();
            occupationContext.arc(roomX, roomZ, circleRadius, 0, 2 * Math.PI);
            occupationContext.fill();
          }
        }

        if (roomX !== undefined && roomZ !== undefined) {
          const color1 = addAlpha(temperature, 1.0),
            color2 = addAlpha(lightenColor(temperature, fadeIntensity), 0.9),
            color3 = addAlpha(
              lightenColor(temperature, fadeIntensity * 2),
              0.7
            ),
            color4 = addAlpha(
              lightenColor(temperature, fadeIntensity * 3),
              0.5
            ),
            color5 = addAlpha(
              lightenColor(temperature, fadeIntensity * 4),
              0.3
            ),
            color6 = addAlpha(
              lightenColor(temperature, fadeIntensity * 5),
              0.1
            ),
            gradient = temperatureContext.createRadialGradient(
              roomX,
              roomZ,
              0,
              roomX,
              roomZ,
              circleRadius
            );
          gradient.addColorStop(0, color1);
          gradient.addColorStop(0.2, color2);
          gradient.addColorStop(0.4, color3);
          gradient.addColorStop(0.6, color4);
          gradient.addColorStop(0.8, color5);
          gradient.addColorStop(1, color6);

          temperatureContext.fillStyle = gradient;
          temperatureContext.beginPath();
          temperatureContext.arc(roomX, roomZ, circleRadius, 0, 2 * Math.PI);
          temperatureContext.fill();
        }
      });

      const temperatureHeatmapTexture = new THREE.CanvasTexture(
        temperatureCanvas
      );
      temperatureHeatmapTexture.needsUpdate = true;

      const occupationHeatmapTexture = new THREE.CanvasTexture(
        occupationCanvas
      );
      occupationHeatmapTexture.colorSpace = THREE.SRGBColorSpace;
      occupationHeatmapTexture.needsUpdate = true;

      switch (type) {
        case "temperature":
          objLoader.load(
            ceilingSrc,
            (obj) => ceillingObj(obj, scene, temperatureHeatmapTexture, 0.6),
            undefined,
            (error) => {
              throw new Error(
                `An error occurred while loading the OBJ file: ${error}`
              );
            }
          );
          heatmap = true;
          break;
        case "occupation":
          if (sensorWithOccupation) {
            objLoader.load(
              ceilingSrc,
              (obj) => ceillingObj(obj, scene, occupationHeatmapTexture, 0.5),
              undefined,
              (error) => {
                throw new Error(
                  `An error occurred while loading the OBJ file: ${error}`
                );
              }
            );
            heatmap = true;
            break
          } else {
            break;
          }
        default:
          heatmap = false;
          break;
      }
    },
    undefined,
    (error) => {
      throw new Error(`An error occurred while loading the OBJ file: ${error}`);
    }
  );
}

export const floorStructure = (obj, scene, floorValues, heatmap) => {
  const textureLoader = new THREE.TextureLoader();

  const textures = {
    emeExt: textureLoader.load(floorValues.emergencyExt),
    emeInt: textureLoader.load(floorValues.emergencyInt),
    esc01: textureLoader.load(floorValues.stairs01),
    esc02: textureLoader.load(floorValues.stairs02),
    topoEnt: textureLoader.load(floorValues.bathrooms),
  };

  Object.values(textures).forEach((texture) => {
    texture.colorSpace = THREE.SRGBColorSpace;
  });

  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      let materialOptions = {
        transparent: true,
        color: 0xffffff,
        specularIntensity: 1,
        reflectivity: 1,
        roughness: 0.2,
        opacity: 0.2,
        depthWrite: true,
        depthTest: true,
        blending: THREE.NormalBlending,
      };

      switch (child.name) {
        case "emergenciaExt":
          materialOptions.alphaMap = textures.emeExt;
          materialOptions.color = 0x000000;
          materialOptions.opacity = 1;
          break;
        case "emergenciaInt":
          materialOptions.map = textures.emeInt;
          materialOptions.transparent = false;
          materialOptions.opacity = 1;
          break;
        case "escadas_01":
          materialOptions.map = textures.esc01;
          materialOptions.transparent = false;
          materialOptions.opacity = 1;
          break;
        case "escada_02":
          materialOptions.map = textures.esc02;
          materialOptions.transparent = false;
          materialOptions.opacity = 1;
          break;
        case "elevador.banheiros":
          materialOptions.map = textures.topoEnt;
          materialOptions.transparent = false;
          materialOptions.opacity = 1;
          break;
        default:
          break;
      }

      if (child.name != "parede" && child.name != "emergenciaExt" && heatmap) {
        child.scale.y = 1.22;
      }

      child.material = new THREE.MeshPhysicalMaterial(materialOptions);
      child.renderOrder = 1;
    }
  });

  scene.add(obj);
};

export const floorObj = (obj, scene, texAlpha, texOcclusion) => {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.material = new THREE.MeshPhysicalMaterial({
        transparent: 1,
        alphaMap: texAlpha,
        map: texOcclusion,
        color: "#ffffff",
        specularIntensity: 1,
        reflectivity: 0.8,
        roughness: 1,
      });
      child.scale.x = 1;
      child.scale.z = 1;

      child.renderOrder = 0;
    }
  });
  scene.add(obj);
};

//#endregion
export { renderSimpleObjects, cleanRenders, renderFloor, orbitCamera };
